"use client";

/**
 * Key risks — the caveats a reader has to carry alongside the figures.
 *
 * Extracted from the design source `compass-oppreport.jsx` (the `risks`
 * section): `.rv-risklist` for the 13px-gapped column and `.rv-hl` for each
 * item, a 4px bullet drawn with `::before` plus its superscript citation.
 *
 * This lived as half of the Overview's Highlights / Key risks split during the
 * port. The design gives it a section of its own between Metrics and Sources,
 * which is where it is now.
 *
 * Risks are not modelled on real data today (plan §5 gap #8), so an empty
 * array renders an explicit empty state rather than an empty list.
 */

import { ReportCitationMarker } from "./ReportCitationMarker";
import type { ReportClaim } from "./report-types";

export interface ReportKeyRisksProps {
  risks: ReportClaim[];
  onOpenCitation?: (n: number) => void;
}

/** `.rv-hl` — one claim with its 4px bullet and superscript citation marker. */
const RISK_ITEM = [
  "relative pl-[11px]",
  "text-[12.5px] leading-[1.55] text-(--fg-secondary)",
  "before:absolute before:left-0 before:top-2 before:h-1 before:w-1",
  "before:rounded-full before:bg-(--border-strong) before:content-['']",
].join(" ");

export function ReportKeyRisks({ risks, onOpenCitation }: ReportKeyRisksProps) {
  if (risks.length === 0) {
    return (
      <p className="m-0 text-[12.5px] leading-[1.55] text-(--fg-muted)">
        No risks have been captured for this opportunity yet.
      </p>
    );
  }

  return (
    // `.rv-risklist`
    <ul className="m-0 flex list-none flex-col gap-[13px] p-0">
      {/* Risks arrive ordered most → least material and that order is the
          content — never sort or re-group this list. */}
      {risks.map((risk) => (
        <li key={risk.text} className={RISK_ITEM}>
          {risk.text}
          {risk.citation != null && (
            <ReportCitationMarker n={risk.citation} onClick={onOpenCitation} />
          )}
        </li>
      ))}
    </ul>
  );
}
